import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ExternalLink from '../components/ExternalLink';
import { OFFER_NO_BRAINMAPPING_SEATS_URL } from '../config';
import { appendUtmParams, captureMarketingParamsFromUrl } from '../utils/utm-params';
import { trackQuizLeadStep, trackQuizSuccess } from '../utils/facebookPixel';
import { submitQuizCompletion } from '../quizApi';
import '../styles/SpeakerQuizPage.css';

const QUIZ_SPEAKER_IMAGES = {
  'eric-verdin': '/assets/quiz/dr-eric-verdin.jpg',
  'antonella-santuccione': '/assets/quiz/dr-antonella-santuccione-chadha.jpeg',
  'peter-attia': '/assets/quiz/dr-peter-attia.jpeg',
  'seung-wan-kang': '/assets/quiz/dr-seung-wan-kang.webp',
};

const SPEAKERS = [
  {
    id: 'eric-verdin',
    name: 'Dr. Eric Verdin',
    title: 'CEO Buck Institute · nutriție, NAD+ și inflammaging',
    image: QUIZ_SPEAKER_IMAGES['eric-verdin'],
    outcome: 'wrong',
  },
  {
    id: 'antonella-santuccione',
    name: 'Dr. Antonella Santuccione Chadha',
    title: 'Expertă Alzheimer · medicină de precizie pe sex și gen',
    image: QUIZ_SPEAKER_IMAGES['antonella-santuccione'],
    outcome: 'wrong',
  },
  {
    id: 'peter-attia',
    name: 'Dr. Peter Attia',
    title: 'Medicina 3.0 · longevitate și prevenția declinului cognitiv',
    image: QUIZ_SPEAKER_IMAGES['peter-attia'],
    outcome: 'wrong',
  },
  {
    id: 'seung-wan-kang',
    name: 'Dr. Seung Wan Kang',
    title: 'CEO iMediSync · qEEG și neuromodulare',
    image: QUIZ_SPEAKER_IMAGES['seung-wan-kang'],
    outcome: 'correct',
  },
];

function IconUser() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconMail() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 6h16v12H4V6z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M4 7l8 6 8-6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconLock() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M8 11V8a4 4 0 118 0v3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconTicket() {
  return (
    <svg className="speaker-quiz-offer-icon" viewBox="0 0 48 48" fill="none" aria-hidden="true">
      <path
        d="M8 14h32v20H8V14z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path d="M20 14v20M14 20h4M14 26h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <circle cx="32" cy="24" r="3" fill="currentColor" />
    </svg>
  );
}

function ProgressSteps({ step }) {
  const step1Class = step >= 1 ? (step > 1 ? 'is-done' : 'is-active') : '';
  const step2Class = step >= 2 ? 'is-active' : '';
  const lineClass = step > 1 ? 'is-done' : '';

  return (
    <>
      <p className="speaker-quiz-progress-label">
        PASUL {Math.min(step, 2)} DIN 2
      </p>
      <div className="speaker-quiz-progress-track">
        <div className={`speaker-quiz-progress-step ${step1Class}`}>
          <div className="speaker-quiz-progress-dot">1</div>
          <span>Datele tale</span>
        </div>
        <div className={`speaker-quiz-progress-line ${lineClass}`} aria-hidden="true" />
        <div className={`speaker-quiz-progress-step ${step2Class}`}>
          <div className="speaker-quiz-progress-dot">2</div>
          <span>Recomandarea ta</span>
        </div>
      </div>
    </>
  );
}

function StepForm({ fullName, email, onFullName, onEmail, onContinue, canContinue }) {
  return (
    <>
      <ProgressSteps step={1} />
      <h1 className="speaker-quiz-title">
        Intră în quiz și primește bilet la <span className="accent">99 €</span>
      </h1>
      <p className="speaker-quiz-subtitle">
        Ghicește speakerul care va fi prezent la eveniment și deblochează biletul tău la preț
        special.
      </p>

      <div className="speaker-quiz-field">
        <label htmlFor="quiz-full-name">Nume complet</label>
        <div className="speaker-quiz-input-wrap">
          <IconUser />
          <input
            id="quiz-full-name"
            type="text"
            autoComplete="name"
            placeholder="Ex: Andrei Popescu"
            value={fullName}
            onChange={(e) => onFullName(e.target.value)}
          />
        </div>
      </div>

      <div className="speaker-quiz-field">
        <label htmlFor="quiz-email">Email</label>
        <div className="speaker-quiz-input-wrap">
          <IconMail />
          <input
            id="quiz-email"
            type="email"
            autoComplete="email"
            placeholder="Ex: andrei.popescu@email.com"
            value={email}
            onChange={(e) => onEmail(e.target.value)}
          />
        </div>
      </div>

      <button
        type="button"
        className="speaker-quiz-btn speaker-quiz-btn-primary"
        disabled={!canContinue}
        onClick={onContinue}
      >
        Continuă
        <span aria-hidden="true">→</span>
      </button>

      <p className="speaker-quiz-privacy">
        <IconLock />
        Datele tale sunt în siguranță
      </p>
    </>
  );
}

function StepSpeakers({ selectedId, onSelect, onSubmit, canSubmit }) {
  return (
    <>
      <ProgressSteps step={2} />
      <h1 className="speaker-quiz-title">
        Care speaker va fi prezent la eveniment?
      </h1>
      <p className="speaker-quiz-hint">Ghicește și câștigă o reducere de 225€</p>

      <div className="speaker-quiz-options" role="radiogroup" aria-label="Alege un speaker">
        {SPEAKERS.map((speaker) => (
          <button
            key={speaker.id}
            type="button"
            role="radio"
            aria-checked={selectedId === speaker.id}
            className={`speaker-quiz-option ${selectedId === speaker.id ? 'is-selected' : ''}`}
            onClick={() => onSelect(speaker.id)}
          >
            <span className="speaker-quiz-option-radio" aria-hidden="true" />
            {speaker.image ? (
              <img
                src={speaker.image}
                alt={speaker.name}
                className="speaker-quiz-option-avatar"
                loading="lazy"
              />
            ) : (
              <span
                className="speaker-quiz-option-avatar speaker-quiz-option-avatar--placeholder"
                aria-hidden="true"
              >
                {speaker.name
                  .split(' ')
                  .slice(1, 3)
                  .map((w) => w[0])
                  .join('')}
              </span>
            )}
            <span className="speaker-quiz-option-text">
              <strong>{speaker.name}</strong>
              <span>{speaker.title}</span>
            </span>
          </button>
        ))}
      </div>

      <button
        type="button"
        className="speaker-quiz-btn speaker-quiz-btn-primary"
        disabled={!canSubmit}
        onClick={onSubmit}
      >
        Vezi rezultatul
      </button>
    </>
  );
}

function ResultSuccess() {
  const [secondsLeft, setSecondsLeft] = useState(3);
  const checkoutUrl = useMemo(
    () => appendUtmParams(OFFER_NO_BRAINMAPPING_SEATS_URL),
    []
  );

  useEffect(() => {
    const redirectTimer = window.setTimeout(() => {
      window.location.assign(appendUtmParams(OFFER_NO_BRAINMAPPING_SEATS_URL));
    }, 3000);

    const countdownTimer = window.setInterval(() => {
      setSecondsLeft((prev) => (prev > 1 ? prev - 1 : 1));
    }, 1000);

    return () => {
      window.clearTimeout(redirectTimer);
      window.clearInterval(countdownTimer);
    };
  }, []);

  return (
    <div className="speaker-quiz-success-dots">
      <div className="speaker-quiz-result-icon speaker-quiz-result-icon--success" aria-hidden="true">
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
          <path
            d="M5 12l5 5L19 7"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <h1 className="speaker-quiz-result-title">
        Felicitări! <span className="accent-success">Ai ales corect</span>
      </h1>
      <p className="speaker-quiz-result-body">
        Ai identificat speakerul corect și ai deblocat <strong>oferta specială</strong> dedicată
        exclusiv participanților la quiz.
      </p>

      <div className="speaker-quiz-offer">
        <div className="speaker-quiz-offer-inner">
          <IconTicket />
          <div>
            <h3>Bilet Brain Event</h3>
            <p className="speaker-quiz-offer-price-old">Preț normal: 324 €</p>
            <p className="speaker-quiz-offer-price-now">
              Acum: 99 € <small>inclus TVA</small>
            </p>
            <span className="speaker-quiz-offer-badge">
              <span aria-hidden="true">🧠</span> Fără brainmapping
            </span>
          </div>
        </div>
      </div>

      <ExternalLink
        href={checkoutUrl}
        className="speaker-quiz-btn speaker-quiz-btn-primary"
      >
        Rezervă biletul la 99 €
        <span aria-hidden="true">→</span>
      </ExternalLink>

      <p className="speaker-quiz-offer-note">
        <span aria-hidden="true">⏱</span>
        Te redirecționăm automat în {secondsLeft} {secondsLeft === 1 ? 'secundă' : 'secunde'}…
      </p>
    </div>
  );
}

function ResultWrong({ onRetry }) {
  return (
    <>
      <div className="speaker-quiz-result-icon speaker-quiz-result-icon--info" aria-hidden="true">
        <span style={{ fontSize: '1.75rem', fontWeight: 800 }}>i</span>
      </div>
      <h1 className="speaker-quiz-result-title">
        Ne pare rău — <span className="accent-info">nu anul acesta</span>
      </h1>
      <p className="speaker-quiz-result-body">
        Speakerul pe care l-ai ales nu va fi prezent la ediția din acest an. Poate îl vom vedea
        anul viitor! Încearcă din nou.
      </p>

      <button type="button" className="speaker-quiz-btn speaker-quiz-btn-primary" onClick={onRetry}>
        <span aria-hidden="true">↻</span> Încearcă din nou
      </button>
    </>
  );
}

function SpeakerQuizPage() {
  useEffect(() => {
    captureMarketingParamsFromUrl();
  }, []);

  const [step, setStep] = useState(1);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedSpeakerId, setSelectedSpeakerId] = useState('');
  const [outcome, setOutcome] = useState(null);

  const emailValid = useMemo(() => {
    const v = email.trim();
    return v.length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  }, [email]);

  const canContinueStep1 = fullName.trim().length >= 2 && emailValid;

  const handleContinue = () => {
    if (!canContinueStep1) return;
    trackQuizLeadStep();
    setStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmitSpeaker = () => {
    const speaker = SPEAKERS.find((s) => s.id === selectedSpeakerId);
    if (!speaker) return;
    if (speaker.outcome === 'correct') {
      trackQuizSuccess();
    }
    void submitQuizCompletion({
      name: fullName,
      email,
      speakerId: speaker.id,
      outcome: speaker.outcome,
    });
    setOutcome(speaker.outcome);
    setStep(3);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleRetry = () => {
    setOutcome(null);
    setSelectedSpeakerId('');
    setStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="speaker-quiz-page">
      <main className="speaker-quiz-main">
        <div className="speaker-quiz-stack">
          <Link to="/" className="speaker-quiz-back">
            <span aria-hidden="true">←</span>
            Back to home
          </Link>
          <div className="speaker-quiz-card">
          {step === 1 && (
            <StepForm
              fullName={fullName}
              email={email}
              onFullName={setFullName}
              onEmail={setEmail}
              onContinue={handleContinue}
              canContinue={canContinueStep1}
            />
          )}
          {step === 2 && (
            <StepSpeakers
              selectedId={selectedSpeakerId}
              onSelect={setSelectedSpeakerId}
              onSubmit={handleSubmitSpeaker}
              canSubmit={!!selectedSpeakerId}
            />
          )}
          {step === 3 && outcome === 'correct' && <ResultSuccess />}
          {step === 3 && outcome === 'wrong' && (
            <ResultWrong onRetry={handleRetry} />
          )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default SpeakerQuizPage;
